import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// styles
import * as styles from "./Carousel.module.css";

// icons
import leftArrow from "../../images/icons/left_arrw.png";
import rightArrow from "../../images/icons/right_arrw.png";

const Carousel = ({
  items = [],
  slidesToShow = 3,
  slidesToShowTablet = 2,
  slidesToShowMobile = 1,
  gap = 24,
  autoPlay = false,
  autoPlayInterval = 3000,
  showDots = true,
  showArrows = true,
  arrowPosition = "bottom-right", // "bottom-right" | "center"
  renderItem,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slidesToShowCurrent, setSlidesToShowCurrent] = useState(slidesToShow);
  const [isMobile, setIsMobile] = useState(false);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Calculate total slides based on screen size
  const totalSlides = items.length;
  const maxIndex = Math.max(0, totalSlides - slidesToShowCurrent);

  // Handle responsive slides
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 767) {
        setSlidesToShowCurrent(slidesToShowMobile);
        setIsMobile(true);
      } else if (width <= 991) {
        setSlidesToShowCurrent(slidesToShowTablet);
        setIsMobile(false);
      } else {
        setSlidesToShowCurrent(slidesToShow);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [slidesToShow, slidesToShowTablet, slidesToShowMobile]);

  // Auto play functionality
  useEffect(() => {
    if (autoPlay) {
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          // Loop back to start when reaching the end
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, maxIndex]);

  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setTimeout(() => setIsTransitioning(false), 600);

    // Reset autoplay timer when manually navigating
    if (autoPlay && autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);
    }
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    setTimeout(() => setIsTransitioning(false), 600);

    // Reset autoplay timer when manually navigating
    if (autoPlay && autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);
    }
  };

  const handleDotClick = index => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 600);

    // Reset autoplay timer when manually navigating
    if (autoPlay && autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);
    }
  };

  // Calculate transform
  const slideWidth = 100 / slidesToShowCurrent;
  // Calculate the offset including gaps for desktop
  const gapOffset = isMobile ? 0 : currentIndex * gap;
  const transform = isMobile
    ? "none"
    : `translateX(calc(-${currentIndex * slideWidth}% - ${gapOffset}px))`;

  // Generate dots based on visible groups
  const totalDots = maxIndex + 1;

  // For mobile, only show current item
  const displayItems = isMobile ? [items[currentIndex]] : items;

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.carouselContainer} ref={carouselRef}>
        <div
          className={styles.carouselTrack}
          style={{
            transform,
            transition:
              isTransitioning && !isMobile
                ? "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                : "none",
          }}
        >
          {displayItems.map((item, index) => {
            const actualIndex = isMobile ? currentIndex : index;
            return (
              <div
                key={actualIndex}
                className={styles.carouselSlide}
                style={{
                  flex: isMobile
                    ? "0 0 100%"
                    : `0 0 calc(${slideWidth}% - ${gap}px)`,
                  marginRight:
                    !isMobile && index < items.length - 1 ? `${gap}px` : "0",
                }}
              >
                {renderItem ? renderItem(item, actualIndex) : item}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Controls */}
      <div
        className={`${styles.carouselControls} ${
          arrowPosition === "center"
            ? styles.controlsCenter
            : styles.controlsBottomRight
        }`}
      >
        {/* Dots */}
        {showDots && totalDots > 1 && (
          <div className={styles.carouselDots}>
            {Array.from({ length: totalDots }).map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${
                  index === currentIndex ? styles.dotActive : ""
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Arrow Buttons */}
        {showArrows && totalSlides > slidesToShowCurrent && (
          <div className={styles.carouselArrows}>
            <button
              className={`${styles.arrowButton} ${styles.arrowPrev} ${
                currentIndex === 0 ? styles.arrowDisabled : ""
              }`}
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              aria-label="Previous slide"
            >
              <img src={leftArrow} alt="Previous" />
            </button>
            <button
              className={`${styles.arrowButton} ${styles.arrowNext} ${
                currentIndex === maxIndex ? styles.arrowDisabled : ""
              }`}
              onClick={handleNext}
              disabled={currentIndex === maxIndex}
              aria-label="Next slide"
            >
              <img src={rightArrow} alt="Next" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

Carousel.propTypes = {
  items: PropTypes.array.isRequired,
  slidesToShow: PropTypes.number,
  slidesToShowTablet: PropTypes.number,
  slidesToShowMobile: PropTypes.number,
  gap: PropTypes.number,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
  showDots: PropTypes.bool,
  showArrows: PropTypes.bool,
  arrowPosition: PropTypes.oneOf(["bottom-right", "center"]),
  renderItem: PropTypes.func,
};

export default Carousel;
